import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import config from './config';
import { Security } from '@okta/okta-react';
import {
  GelPageLayout,
  GelPageContent,
  GelPageFooter,
  GelPageHeader,
  GelPageSideNav,
  GelScreenDetectorContext,
  GelSpinner
} from '@tal-gel/components';
import Header from "../src/components/header"
import Footer from "../src/components/footer"
import AppRoutes from './pages/app.routing';
import { getGelTokens } from '@tal-gel/theming';
import { useCookies } from 'react-cookie';
import { PageSideNavStyle, PageSideNavXsStyle } from "../src/components/nav-menu/styles/nav.styles";
import PageNavigation from "../src/components/nav-menu/page-nav";
import { HttpStatusCode } from 'axios';
import { ADOBE_ANALYTICS_SOURCE, THEME_NAME } from './constants/constants';
import { GET_MEMBER_DATA_QUERY } from './graphql/queries/graphql-queries.constant';
import { UserContext } from './common/usercontext/user.context';
import { MemberRecord, HistoricalPaymentDetail, ScheduledPaymentDetail } from './common/models/memberclaims';
import { SET_USER_SESSION } from './graphql/queries/graphql-mutations';
import SESSION_STORAGE_KEY from './constants/storage.constant';
import useUserIdleTimer from './common/useractivity/useUserIdleTimer';
import TimeOutModal from "../src/components/TimeOutModal";
import { loadScript } from './common/analytics/load-script';
import { NOTIFICATION_PREFERENCES } from './constants/notificationPreferences.constant';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { ThemeContext } from './common/usercontext/themes.context';
import HttpClientService from './common/api/http-client.provider';
import { injectGAScript } from './common/analytics/googleAnalytics';
import { Helmet } from 'react-helmet-async';
import { ErrorPageStatus } from './common/models/enums/errorPageStatus.model';
import MmcGraphQL from './common/api/mmc-graphql';
import PendingStagedFilesModal from './pages/claimdetails/documentsupload/PendingStagedFilesModal';
import { GelScrollOnMount } from './common/components/GelScrollOnMount';

const oktaAuth = new OktaAuth(config.oidc);

const App = () => {
  const [queryClient] = useState(() => new QueryClient());
  const navigate = useNavigate();
  const [{ aid, tpid, ssoid, rid }, setssoCookies] = useCookies(['aid', 'rid', 'ssoid', 'tpid']);

  const { screen, isXsScreen } = useContext(GelScreenDetectorContext) as GelScreenDetectorContext;
  const { contextData, setUserContext } = useContext(UserContext)
  const [memberDataLoaded, setmemberDataLoaded] = useState(false);
  const [showNavigation, setshowNavigation] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const { setThemeContext } = useContext(ThemeContext);
  const { pathname } = useLocation();

  const handleIdle = () => {
    setOpenModal(true);
  };

  useUserIdleTimer(10, handleIdle);

  const restoreOriginalUri = useCallback(
    (oktaAuth: OktaAuth, originalUri: string) => {
      navigate(toRelativeUrl(originalUri || '/', window.location.origin), {
        replace: true,
      })
    },
    [],
  );

  const initialLoadDone = useRef(false);

  useEffect(() => {
    const fetchData = async () => {
      console.log('Initial load...')
      setmemberDataLoaded(false);

      try {
        await loadScript(ADOBE_ANALYTICS_SOURCE);

        if (aid && rid) {
          const createUserSessionResponse = await MmcGraphQL(SET_USER_SESSION);

          if (!createUserSessionResponse?.sessionCreated) {
            navigate("/error", { state: { errorCode: HttpStatusCode.Forbidden } });
            return;
          }

          if (!tpid) {
            setssoCookies('tpid', createUserSessionResponse.superFundPrefix.toLowerCase());
          }

          if (createUserSessionResponse.theme) {
            setThemeContext({ theme: createUserSessionResponse.theme, fund: THEME_NAME.TAL });
          }

          sessionStorage.setItem(createUserSessionResponse.superFundPrefix.toLowerCase(), createUserSessionResponse.adobeAnalyticsSuperfundMapping);
          window.sessionStorage.setItem(SESSION_STORAGE_KEY.SESSIONID_TOKEN, createUserSessionResponse.sessionIdToken);

          if (createUserSessionResponse.googleAnalyticsId) {
            injectGAScript(createUserSessionResponse.googleAnalyticsId);
          }

          const memberRecord = await MmcGraphQL(GET_MEMBER_DATA_QUERY);
          updateUserContext(memberRecord);
          setmemberDataLoaded(true);
          initialLoadDone.current = true;

          if (memberRecord?.isDuplicatedMemberLocated) {
            navigate("/error", { state: { errorCode: ErrorPageStatus.DUPLICATE_MEMBER_FOUND } });
            return;
          }

          if (memberRecord?.claims?.length) {
            const unfinishedClaim = memberRecord.claims.some(claim => claim.status.description !== 'Claim finalised');
            unfinishedClaim ? setshowNavigation(true) : navigate("/noClaims");
          } else {
            navigate("/noClaims");
          }
        } else {
          setmemberDataLoaded(true);
        }
      } catch (error) {
        setmemberDataLoaded(true);
        console.error('Script loading or GraphQL error:', error);
        navigate("/noClaims");
      }
    };

    if (!initialLoadDone.current)
      fetchData();
  }, [aid, tpid, rid, ssoid]);
  
  // Refresh effect
  useEffect(() => {
    if (initialLoadDone.current && aid && rid) {
      const refreshSession = async () => {
        try {
          console.log('Refresh session')
          const createUserSessionResponse = await MmcGraphQL(SET_USER_SESSION);
          window.sessionStorage.setItem(SESSION_STORAGE_KEY.SESSIONID_TOKEN, createUserSessionResponse.sessionIdToken);
          // Optionally update session storage or context if needed
        } catch (error) {
          console.error('Session refresh error:', error);
        }
      };
      refreshSession();
    }
  }, [aid, rid]);

  const updateUserContext = (memberRecord) => {
    let memberDetails: MemberRecord = {};
    memberDetails.memberNumber = memberRecord?.memberNumber;
    memberDetails.policyManagerId = memberRecord?.policyManagerId;
    memberDetails.memberId = memberRecord?.memberId;
    memberDetails.displayLegalDisclosure = memberRecord?.displayLegalDisclosure;
    memberDetails.unReadDigiCorroMessageCount = memberRecord?.unReadDigiCorroMessageCount;
    memberDetails.notificationPreference = NOTIFICATION_PREFERENCES[memberRecord?.notificationPreference];
    setUserContext({
      claims: memberRecord?.claims,
      yourDetails: memberRecord?.profile,
      memberDetails: memberDetails,
      otherDocuments: memberRecord?.documentsHistory?.value.filter((doc: any) => doc.isAssociatedRequirementFound === false),
      historicalPayments: memberRecord?.historicalPayments as HistoricalPaymentDetail[],
      scheduledPayments: memberRecord?.scheduledPayments as ScheduledPaymentDetail[],
    });
  }

  return (
     <>
      <Helmet>
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=2.0" />
      </Helmet>
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <HttpClientService>
        <QueryClientProvider client={queryClient}>
          <GelPageLayout >
            <GelPageHeader>
              {memberDataLoaded && <Header />}
            </GelPageHeader>
            {showNavigation && !contextData.memberDetails?.displayLegalDisclosure &&
              <GelPageSideNav sliding miniOnClose topOnMobile maxHeightAtTop={getGelTokens().global.sizeBaseUnit * 120}
                style={{ ...!isXsScreen(screen) ? PageSideNavStyle : PageSideNavXsStyle }}  >
                <PageNavigation />
              </GelPageSideNav>
            }
            <GelPageContent>
              {openModal && <TimeOutModal />}
              <PendingStagedFilesModal/>
              <GelScrollOnMount key={pathname} />
              <AppRoutes />
              {!memberDataLoaded && <GelSpinner medium overlay />}
              <Outlet />
            </GelPageContent>
            <GelPageFooter>
              {memberDataLoaded && <Footer />}
            </GelPageFooter>
          </GelPageLayout>
        </QueryClientProvider>
      </HttpClientService>
    </Security>
    </>
  );
}

export default App;